import React from 'react'

import {Hit, SearchResponse} from '@algolia/client-search'
import Link from 'next/link'
import WebpImage from 'src/components/common/WebpImage'
import styled from 'styled-components'
import {palette} from 'styled-tools'

import {VariantList} from '@festi/common/api/rest'
import {ProductPrices} from '@festi/common/components/cart/ProductPrices'
import {H5, P} from '@festi/common/components/typography'
import settings from '@festi/common/constants/settings'
import {AugmentedUserPrice} from '@festi/common/utils/rest'
import {sendSearchClickEvent} from '@festi/common/utils/tagmanager'

import {InsightType} from './QuickSearchHits'

const Products = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ProductLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 6px 12px;
  border-radius: 8px;

  &:hover {
    background-color: ${palette('ui4Solid')};
  }
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  background-color: transparent;
`

const Image = styled.div`
  width: 68px;
  height: 68px;
  background-color: transparent;
`

const Texts = styled.div`
  width: 100%;
  ${P} {
    color: ${palette('ui80Solid')};
    padding-bottom: 8px;
  }
`

const SeeMore = styled.div`
  font-weight: 500;
  padding-top: 16px;
  padding-left: 5px;
  text-decoration: underline;
`

const EmptyResultsText = styled(P)`
  color: ${palette('ui40Solid')};
`

interface ProductProp {
  query: string
  variant: Hit<VariantList>
  userPrice?: AugmentedUserPrice
  onClick?: () => void
}

function Product({query, variant, userPrice, onClick}: ProductProp) {
  const image = variant.firstImage

  const price =
    userPrice?.price?.price ||
    variant?.listings?.[settings.channel]?.price?.price ||
    0
  const lowestPrice =
    userPrice?.lowestPrice ||
    variant?.listings?.[settings.channel]?.price?.lowestPrice ||
    0
  const discountedPrice =
    userPrice?.userPrice ||
    variant?.listings?.[settings.channel]?.price?.discountedPrice ||
    0

  return (
    <ProductLink
      href={`/vorur/${variant.product.slug}/${variant.sku}`}
      onClick={() => {
        sendSearchClickEvent({
          search_term: query,
          search_type: 'item_suggestion',
          event_label: variant.sku,
        })
        !!onClick && onClick()
      }}
    >
      <ImageContainer>
        <Image>
          <WebpImage
            webp={image.image.webpXs}
            jpg={image.image.jpgXs}
            alt={image.alt || variant.name}
          />
        </Image>
      </ImageContainer>

      <Texts>
        <P bold>{variant.name}</P>
        <ProductPrices
          size="large"
          price={price}
          lowestPrice={lowestPrice}
          discountedPrice={discountedPrice}
        />
      </Texts>
    </ProductLink>
  )
}

interface HitsProps {
  productResults?: SearchResponse<VariantList>
  handleClose?: () => void
  handleQuicksearchClickEvents: (
    name: InsightType,
    id: string,
    position: number,
  ) => void
}

export default function ProductHits({
  productResults,
  handleClose,
  handleQuicksearchClickEvents,
}: HitsProps) {
  const isEmpty = !productResults?.nbHits && !!productResults?.queryID

  return (
    <div>
      <H5 withGutter>
        <span>Niðurstöður fyrir</span>{' '}
        <span translate="no">&ldquo;{productResults?.query}&rdquo;</span>
      </H5>

      {isEmpty ? (
        <EmptyResultsText>Engar niðurstöður</EmptyResultsText>
      ) : (
        <>
          <Products>
            {productResults?.hits?.map((variant, index) => (
              <Product
                key={variant.id}
                query={productResults.query}
                variant={variant}
                onClick={() => {
                  !!handleClose && handleClose()

                  handleQuicksearchClickEvents(
                    InsightType.product,
                    variant.objectID,
                    index + 1,
                  )
                }}
              />
            ))}
          </Products>

          <SeeMore>
            <Link href={`/leit?q=${productResults?.query}`}>Sjá allt</Link>
          </SeeMore>
        </>
      )}
    </div>
  )
}
